import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { formatToLocalDateStr, formatToLocalDisplayDate } from "../../../utils/dateUtils";

const KPICalendar = () => {
  const dispatch = useDispatch();
  const { myWorks, loading } = useSelector((state) => state.kpi);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    dispatch.kpi.getMyWorks({ status: 'all', limit: 500 });
  }, [dispatch]);

  const works = myWorks?.works || [];

  // Group works by due date (YYYY-MM-DD) using local date utility consistent with KPIWorks
  const worksByDate = works.reduce((acc, work) => {
    const dueDateStr = work.due_date || work.task_end_date || work.task_due_date || work.due_at;
    if (dueDateStr) {
      const dateKey = formatToLocalDateStr(dueDateStr);
      if (dateKey) {
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(work);
      }
    }
    return acc;
  }, {});

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long' });

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(year, month, day));
  }

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayStr = formatToLocalDateStr(new Date());

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Calendar</h1>
          <p className="text-slate-500 text-sm">Calendar view of KPI works and due dates</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleToday}
            className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 shadow-2xs transition-colors cursor-pointer"
          >
            Today
          </button>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1 shadow-2xs">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors cursor-pointer"
              title="Previous Month"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <span className="text-sm font-bold text-slate-900 px-3 min-w-[130px] text-center">
              {monthName} {year}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors cursor-pointer"
              title="Next Month"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Days Header */}
        <div className="grid grid-cols-7 border-b border-slate-200 bg-[#f7f6f3] text-center py-2.5">
          {daysOfWeek.map((day) => (
            <div key={day} className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 divide-x divide-y divide-slate-100 min-h-[500px]">
          {calendarDays.map((dateObj, idx) => {
            if (!dateObj) {
              return <div key={`empty-${idx}`} className="bg-slate-50/30 p-2"></div>;
            }

            const dateStr = formatToLocalDateStr(dateObj);
            const isToday = dateStr === todayStr;
            const dayWorks = worksByDate[dateStr] || [];

            return (
              <div key={dateStr} className={`p-2 transition-colors min-h-[95px] ${isToday ? 'bg-blue-50/20' : 'hover:bg-slate-50/50'}`}>
                <div className="flex justify-between items-center mb-1.5">
                  <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-slate-900 text-white shadow-xs' : 'text-slate-700'}`}>
                    {dateObj.getDate()}
                  </span>
                  {dayWorks.length > 0 && (
                    <span className="text-[10px] font-bold text-slate-400">
                      {dayWorks.length} {dayWorks.length === 1 ? 'work' : 'works'}
                    </span>
                  )}
                </div>

                <div className="space-y-1 overflow-y-auto max-h-[70px] scrollbar-none">
                  {dayWorks.map((w) => {
                    const workTitle = w.title || w.task_name || "Untitled Work";
                    const st = (w.status || w.status_name || w.task_status || '').toString().toLowerCase();
                    const isCompleted = st.includes('completed') || st.includes('done') || st.includes('finished');
                    const isReview = st.includes('review') || st.includes('submitted');
                    const workDueDate = w.due_date || w.task_end_date || w.task_due_date || w.due_at;
                    const formattedDueDate = formatToLocalDisplayDate(workDueDate, "MMM DD, YYYY");

                    return (
                      <Link
                        key={w.id || w._id}
                        to={`/kpi/works/${w.id || w._id}`}
                        className={`block text-[11px] font-medium p-1 rounded border truncate transition-all ${
                          isCompleted
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:border-emerald-300'
                            : isReview
                            ? 'bg-purple-50 text-purple-800 border-purple-200 hover:border-purple-300'
                            : 'bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-300'
                        }`}
                        title={`${workTitle}${formattedDueDate ? ` (Due: ${formattedDueDate})` : ''}`}
                      >
                        {workTitle}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default KPICalendar;
